import React from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { ticketColors } from '../../shared/theme/ticketColors';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import { catalogProductFormStyles as styles } from './catalogProductForm.styles';
import { CatalogProductFormSegmentedRow } from './CatalogProductFormSegmentedRow';
import type { CatalogProductFormFieldsProps } from './catalogProductFormFields.types';
import { ProductImagePicker } from './ProductImagePicker';

type Props = Pick<
  CatalogProductFormFieldsProps,
  | 'draft'
  | 'onNameChange'
  | 'onItemTypeChange'
  | 'onOpenBrandPicker'
  | 'onOpenCategoryPicker'
  | 'categoryOptions'
  | 'canCreateCategory'
  | 'onCreateCategory'
  | 'brandOptions'
  | 'nameInputRef'
  | 'mobile'
  | 'desktopRail'
  | 'desktopQuickMode'
  | 'imageUrl'
  | 'onPickImage'
  | 'onRemoveImage'
  | 'videoUrl'
  | 'onPickVideo'
  | 'onRemoveVideo'
  | 'videoUploading'
  | 'imageUploading'
> & {
  compact?: boolean;
};

const ITEM_TYPE_OPTIONS = [
  { value: 'physical' as const, label: 'Producto' },
  { value: 'service' as const, label: 'Servicio' },
];

export function CatalogProductFormIdentityFields({
  draft,
  onNameChange,
  onItemTypeChange,
  onOpenBrandPicker,
  onOpenCategoryPicker,
  categoryOptions,
  canCreateCategory = false,
  onCreateCategory,
  brandOptions = [],
  nameInputRef,
  imageUrl,
  onPickImage,
  onRemoveImage,
  videoUrl,
  onPickVideo,
  onRemoveVideo,
  videoUploading,
  imageUploading,
  mobile = false,
  desktopRail = false,
  desktopQuickMode = false,
  compact = false,
}: Props) {
  const serviceCatalogEnabled = useHasBusinessCapability('service_catalog');

  const brandLabel = draft.brandId
    ? brandOptions.find((b) => b.id === draft.brandId)?.name ?? 'Marca'
    : 'Elegir…';

  const categoryLabel =
    categoryOptions.find((option) => option.id === draft.categoryId)?.label ?? 'Elegir…';

  const categorySelectorCopy =
    draft.itemType === 'service'
      ? 'No hay categorías de servicio activas. Activá alguna en Categorías.'
      : 'No hay categorías de producto activas. Activá alguna en Categorías.';

  const noCategories = categoryOptions.filter((c) => c.id !== 'all').length === 0;

  const activeVideoUrl = (videoUrl ?? draft.videoUrl ?? '').trim();

  // La foto también sirve en el POS y en la lista — no depende de la tienda online.
  const imagePicker = !desktopQuickMode && onPickImage && onRemoveImage ? (
    <View>
      <ProductImagePicker
        imageUrl={imageUrl}
        onPickImage={onPickImage}
        onRemoveImage={onRemoveImage}
        uploading={imageUploading}
        desktopRail={desktopRail}
      />
      {onPickVideo && onRemoveVideo ? (
        <View style={styles.ticketFormField}>
          <Text style={styles.ticketFormLabel}>Video del producto (opcional)</Text>
          <Text style={styles.ticketFormHint}>
            Subí un video corto para la tienda online.
          </Text>
          <View style={{ gap: 6 }}>
            <TouchableOpacity
              style={[styles.ticketFormInput, { minHeight: 40, justifyContent: 'center', opacity: videoUploading ? 0.6 : 1 }]}
              onPress={onPickVideo}
              disabled={videoUploading}
              accessibilityRole="button"
              accessibilityLabel={activeVideoUrl ? 'Cambiar video' : 'Subir video'}
            >
              <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink }}>
                {videoUploading ? 'Subiendo…' : activeVideoUrl ? 'Cambiar video' : 'Subir video'}
              </Text>
            </TouchableOpacity>
            {activeVideoUrl ? (
              <TouchableOpacity
                style={[styles.ticketFormInput, { minHeight: 40, justifyContent: 'center', opacity: videoUploading ? 0.6 : 1 }]}
                onPress={onRemoveVideo}
                disabled={videoUploading}
                accessibilityRole="button"
                accessibilityLabel="Quitar video"
              >
                <Text style={{ fontSize: 12, fontWeight: '600', color: ticketColors.ink3 }}>Quitar video</Text>
              </TouchableOpacity>
            ) : null}
          </View>
        </View>
      ) : null}
    </View>
  ) : null;

  const brandField = (
    <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
      <Text style={styles.ticketFormLabel}>Marca</Text>
      <TouchableOpacity
        style={[
          styles.ticketFormInput,
          styles.pickerTrigger,
          !!draft.brandId && styles.pickerTriggerActive,
        ]}
        onPress={onOpenBrandPicker}
        accessibilityRole="button"
        accessibilityLabel="Elegir marca"
      >
        <Text
          style={[
            styles.pickerTriggerText,
            !draft.brandId && styles.pickerTriggerTextMuted,
          ]}
          numberOfLines={1}
        >
          {brandLabel}
        </Text>
        <Text style={styles.pickerTriggerChev}>▾</Text>
      </TouchableOpacity>
    </View>
  );

  const categoryField = (
    <View style={[styles.ticketFormField, !mobile && styles.fieldGridItem]}>
      <Text style={styles.ticketFormLabel}>Categoría</Text>
      <TouchableOpacity
        style={[
          styles.ticketFormInput,
          styles.pickerTrigger,
          !!draft.categoryId && styles.pickerTriggerActive,
        ]}
        onPress={onOpenCategoryPicker}
        accessibilityRole="button"
        accessibilityLabel="Elegir categoría"
      >
        <Text
          style={[
            styles.pickerTriggerText,
            !draft.categoryId && styles.pickerTriggerTextMuted,
          ]}
          numberOfLines={1}
        >
          {categoryLabel}
        </Text>
        <Text style={styles.pickerTriggerChev}>▾</Text>
      </TouchableOpacity>
      {noCategories ? (
        canCreateCategory && onCreateCategory ? (
          <View style={styles.noCategoriesHint}>
            <Text style={styles.noCategoriesText}>{categorySelectorCopy}</Text>
            <TouchableOpacity
              style={styles.noCategoriesButton}
              onPress={onCreateCategory}
              accessibilityRole="button"
              accessibilityLabel="Ir a categorías"
            >
              <Text style={styles.noCategoriesButtonText}>Ir a categorías</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <Text style={styles.noCategoriesTextMuted}>{categorySelectorCopy}</Text>
        )
      ) : null}
    </View>
  );

  const itemTypeField = serviceCatalogEnabled ? (
    <View style={[styles.ticketFormField, styles.itemTypeField]}>
      <Text style={styles.ticketFormLabel}>Tipo de ítem</Text>
      <CatalogProductFormSegmentedRow
        options={ITEM_TYPE_OPTIONS}
        value={draft.itemType}
        onChange={onItemTypeChange}
        testIDPrefix="catalog.form.item-type"
      />
    </View>
  ) : null;

  if (compact) {
    return (
      <View style={styles.ticketFormSection}>
        {!desktopQuickMode ? <Text style={styles.ticketFormSectionTitle}>Qué vendés</Text> : null}
        {imagePicker}
        <View style={styles.ticketFormField}>
          <Text style={styles.ticketFormLabel}>
            Nombre <Text style={styles.requiredAsterisk}>*</Text>
          </Text>
          <TextInput
            ref={nameInputRef}
            style={styles.ticketFormInput}
            value={draft.name}
            onChangeText={onNameChange}
            placeholder={draft.itemType === 'service' ? 'Ej: Corte hombre' : 'Ej: Coca Cola 1,5 L'}
            placeholderTextColor={ticketColors.ink3}
            testID="catalog.form.name"
          />
        </View>
        {itemTypeField}
        {/* Marca + Categoría: grid on desktop, stack on mobile */}
        <View style={mobile ? styles.fieldGridMobile : styles.fieldGrid}>
          {brandField}
          {categoryField}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.ticketFormSection}>
      {!desktopRail ? <Text style={styles.ticketFormSectionTitle}>Identidad</Text> : null}
      {imagePicker}
      <View style={styles.ticketFormField}>
        <Text style={styles.ticketFormLabel}>
          Nombre <Text style={styles.requiredAsterisk}>*</Text>
        </Text>
        <TextInput
          ref={nameInputRef}
          style={styles.ticketFormInput}
          value={draft.name}
          onChangeText={onNameChange}
          placeholder={draft.itemType === 'service' ? 'Ej: Corte hombre' : 'Ej: Coca Cola 1,5 L'}
          placeholderTextColor={ticketColors.ink3}
          testID="catalog.form.name"
        />
      </View>
      {itemTypeField}
      {/* Marca + Categoría: grid on desktop, stack on mobile */}
      <View style={mobile ? styles.fieldGridMobile : styles.fieldGrid}>
        {brandField}
        {categoryField}
      </View>
    </View>
  );
}
